import React from "react";
import { motion } from "framer-motion";
import { Github, ExternalLink, Sparkles, FolderGit2, CheckCircle, Terminal } from "lucide-react";
import { PROJECTS } from "../data/portfolioData";

export default function Projects() {
  const featuredProject = PROJECTS.find((p) => p.featured);
  const otherProjects = PROJECTS.filter((p) => !p.featured);

  return (
    <section id="projects" className="py-20 relative overflow-hidden bg-white border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 border border-purple-200 text-purple-900 font-mono text-xs font-bold shadow-sm"
          >
            <FolderGit2 size={15} /> SOFTWARE & CLOUD PROJECTS
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900"
          >
            Production Work & <span className="gradient-text-indigo">Software Engineering Showcase</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-slate-800 text-base font-medium leading-relaxed"
          >
            Demonstrated software engineering projects spanning custom enterprise CMS platforms, Python microservices, Kubernetes orchestration, and multi-cloud infrastructure.
          </motion.p>
        </div>

        {/* Featured Project Banner (SHOC Regional Government Project) */}
        {featuredProject && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-14 relative group rounded-3xl p-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-500 shadow-xl overflow-hidden"
          >
            <div className="relative rounded-[22px] bg-white p-8 lg:p-10 border border-slate-200 backdrop-blur-xl grid lg:grid-cols-12 gap-8 items-center">
              {/* Featured Left Content */}
              <div className="lg:col-span-7 space-y-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold bg-amber-100 text-amber-900 border border-amber-300">
                    <Sparkles size={13} /> FEATURED GOVERNMENT PROJECT
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-indigo-100 text-indigo-900 border border-indigo-200">
                    {featuredProject.status}
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-2xl sm:text-3xl font-black text-slate-900">
                    {featuredProject.title}
                  </h3>
                  <p className="text-xs font-mono font-bold text-indigo-700 uppercase">{featuredProject.category}</p>
                </div>

                <p className="text-slate-800 text-sm sm:text-base font-medium leading-relaxed">
                  {featuredProject.desc}
                </p>

                {/* Highlights List */}
                <div className="grid sm:grid-cols-2 gap-2 pt-2">
                  {featuredProject.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-900 font-bold font-mono">
                      <CheckCircle size={15} className="text-emerald-600 shrink-0" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>

                {/* Tech Stack Pills */}
                <div className="flex flex-wrap gap-2 pt-2">
                  {featuredProject.tags.map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1 rounded-lg text-xs font-mono font-bold bg-slate-100 text-slate-800 border border-slate-300"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* Action Links */}
                <div className="flex items-center gap-4 pt-4">
                  <a
                    href={featuredProject.github}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs text-white bg-indigo-600 hover:bg-indigo-700 transition-all shadow-md"
                  >
                    <Github size={15} /> GitHub Repo
                  </a>
                  <a
                    href={featuredProject.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs text-slate-800 bg-slate-100 border border-slate-300 hover:border-indigo-500 transition-all"
                  >
                    <ExternalLink size={15} /> View Details
                  </a>
                </div>
              </div>

              {/* Featured Right Graphic Showcase */}
              <div className="lg:col-span-5 h-full flex items-center justify-center">
                <div className="w-full h-full min-h-[240px] rounded-2xl bg-indigo-50/80 border border-indigo-200 p-6 flex flex-col justify-between relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-500 shadow-sm">
                  <div className="flex justify-between items-center text-xs font-mono text-slate-800">
                    <span className="flex items-center gap-1.5 font-bold">
                      <Terminal size={15} className="text-indigo-600" /> SADC Government CMS
                    </span>
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  </div>

                  <div className="my-8 text-center space-y-2">
                    <div className="text-4xl font-black text-slate-900">
                      120+ Pages
                    </div>
                    <div className="text-xs text-indigo-900 font-mono font-bold">Delivered in 3 Months Timeline</div>
                  </div>

                  <div className="flex items-center justify-between text-[11px] font-mono font-bold text-slate-800 border-t border-slate-200 pt-3">
                    <span>Docker Containers</span>
                    <span>REST API Integrations</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Project Grid */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {otherProjects.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group relative rounded-3xl p-6 bg-slate-50 border border-slate-200 hover:border-indigo-500 transition-all duration-300 hover:-translate-y-2 shadow-sm hover:shadow-md flex flex-col justify-between"
            >
              <div className="space-y-4">
                {/* Visual Header Graphic */}
                <div
                  className="w-full h-36 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-slate-200 p-4 flex flex-col justify-between relative overflow-hidden group-hover:brightness-105 transition-all"
                >
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-mono uppercase px-2.5 py-0.5 rounded-full bg-white text-indigo-900 border border-indigo-200 font-extrabold">
                      {p.status}
                    </span>
                    <FolderGit2 size={18} className="text-indigo-600" />
                  </div>

                  <div className="text-xs font-mono font-bold text-slate-900 line-clamp-1">
                    {p.category}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                    {p.title}
                  </h4>
                  <p className="text-xs text-slate-800 font-medium leading-relaxed line-clamp-3">
                    {p.desc}
                  </p>
                </div>

                {/* Highlights */}
                <div className="space-y-1 pt-1">
                  {p.highlights.map((h, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 text-[11px] text-slate-900 font-bold font-mono">
                      <span className="text-indigo-600">•</span>
                      <span className="line-clamp-1">{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-slate-200 space-y-4">
                {/* Tech Stack Tags */}
                <div className="flex flex-wrap gap-1.5">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-white text-slate-800 border border-slate-200"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {/* Action Links */}
                <div className="flex items-center justify-between pt-1">
                  <a
                    href={p.github}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800 hover:text-indigo-600 transition-colors"
                  >
                    <Github size={14} /> Repository
                  </a>
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-indigo-700 hover:text-indigo-800 transition-colors"
                  >
                    View <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
